/**
 * usePressAnimation — tactile push-in press feedback for buttons
 *
 * Variants:
 *   standard    → scale 0.96, quick spring  (settings, secondary, cancel)
 *   cta         → scale 0.93, punchy spring (primary CTAs, GO!, Complete, Finish)
 *   destructive → scale 0.97, slow/heavy    (Logout, Delete, Discard)
 *   cc          → scale 0.95, bouncy spring (Cedar chat button — premium feel)
 *
 * Usage:
 *   Wrap any button with <AnimatedPressable variant="cta"> — or use this
 *   hook directly when you need access to animatedStyle/handlers separately.
 */

import { useSharedValue, useAnimatedStyle, withSpring, withTiming } from 'react-native-reanimated';
import * as Haptics from 'expo-haptics';

export type PressVariant = 'standard' | 'cta' | 'destructive' | 'cc';

const VARIANTS: Record<PressVariant, {
  pressedScale: number;
  pressInDuration: number;
  spring: { damping: number; stiffness: number };
  haptic: Haptics.ImpactFeedbackStyle;
}> = {
  standard: {
    pressedScale: 0.96,
    pressInDuration: 80,
    spring: { damping: 15, stiffness: 300 },
    haptic: Haptics.ImpactFeedbackStyle.Light,
  },
  cta: {
    pressedScale: 0.93,
    pressInDuration: 80,
    spring: { damping: 10, stiffness: 400 },
    haptic: Haptics.ImpactFeedbackStyle.Medium,
  },
  destructive: {
    pressedScale: 0.97,
    pressInDuration: 150,
    spring: { damping: 22, stiffness: 150 },
    haptic: Haptics.ImpactFeedbackStyle.Medium,
  },
  cc: {
    pressedScale: 0.95,
    pressInDuration: 80,
    spring: { damping: 6, stiffness: 500 },
    haptic: Haptics.ImpactFeedbackStyle.Light,
  },
};

export function usePressAnimation(variant: PressVariant = 'standard') {
  const scale = useSharedValue(1);
  const config = VARIANTS[variant];

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ scale: scale.value }],
  }));

  const onPressIn = () => {
    scale.value = withTiming(config.pressedScale, { duration: config.pressInDuration });
    Haptics.impactAsync(config.haptic).catch(() => {}); // safe on web/tablets without vibration motor
  };

  const onPressOut = () => {
    scale.value = withSpring(1, config.spring);
  };

  return { animatedStyle, onPressIn, onPressOut };
}
